import {useEffect, useRef, useState} from 'react'
import { usePut,useCurrent, useFetch, useUploadFile, useDeleteWithBody } from '../../hooks'
import { MwSpinnerButton,MwButton, MwInputText, MwTextArea, MwSelector } from '../../components/ui';
import MwInputFile from '../../components/ui/MwInputFile';
import FormLangBar from '../../components/FormLangBar';
import { FaTimes } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';


const MyCompany = ({refreshTenant}) => {
    const {currentLangList,currentCompany,currentCompanyId,currentTenantId,currentLangId} = useCurrent();
    const {data:dataPutResult,loading,putData} = usePut();
    const [formLang,setFormLang] = useState(currentLangId);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileKey, setFileKey] = useState(null);
    const [fileLocation, setFileLocation] = useState(null);
    const [reqAttach, setReqAttach] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const {deleteWithBody} = useDeleteWithBody();
    const [t] = useTranslation('global');

    const {
        location,
        loading: fileUploadLoading,
        handleUpload
    } = useUploadFile(['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml', 'image/bmp', 'image/tiff'], 'companies');

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setReqAttach(file);
            handleUpload(e);
        }
    };

    const handleRemoveFile = async () => {
        setDeleteLoading(true);
        if (fileKey) {
            try {
                await deleteWithBody(`${process.env.REACT_APP_UPLOADS_API_BASE_URL}/delete`, {keyFile: fileKey});
                if (selectedFile?.url) {
                    URL.revokeObjectURL(selectedFile.url);
                }
                setSelectedFile(null);
                setReqAttach(null);
                setFileKey(null);
                setFileLocation(null);
                const fileInput = document.getElementById('file_upload');
                if (fileInput) {
                    fileInput.value = '';
                }
            } catch (error) {
            }
        }
        setDeleteLoading(false);
    };

    const [selectedCurrency, setSelectedCurrency] = useState(null);
    const baseUrl = `${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/currencies/${currentTenantId}/${currentCompanyId}`;
    const { data: currenciesData } = useFetch(baseUrl);
    
    const formatCurrenciesForSelector = () => {
        if (!Array.isArray(currenciesData?.data)) return [];

        return currenciesData.data.flatMap((item) => {
            const currencyCode = String(item?.currency_code || '').trim();
            const currencyRate = String(item?.currency_rate || '').trim();
            const labelAr = String(item?.name_ar || '').trim();
            const labelEn = String(item?.name_en || '').trim();
            const baseItem = {
                _id: item?.id,
                value: currencyCode,
                currency_code: currencyCode,
                currency_rate: currencyRate
            };

            return [
                { ...baseItem, lang: 'ar', label: labelAr || currencyCode },
                { ...baseItem, lang: 'en', label: labelEn || labelAr || currencyCode }
            ];
        });
    };

    const [nameCompany,setNameCompany] = useState({text:'',lang:'en'});
    const [desCompany,setDescCompany] = useState({text:'',lang:'en'});
    const [mainAddress,setMainAddress] = useState({text:'',lang:'en'});
    const commercialRegisterationRef = useRef();
    const businessFieldRef = useRef();
    const vatNumberRef = useRef();
    const mainPhoneRef = useRef();
    const mainEmailRef = useRef();
    const websiteUrlRef = useRef();

    const arrayToObj = (array = []) => {
        return (Array.isArray(array) ? array : []).reduce((obj, item) => {
            if (item?.text) {
                obj[item.lang] = item.text;
            }
            return obj;
        }, {});
    }

    const objToArray = (obj) => {
            const array =   Object.entries(obj).map(([key, value]) => ({ lang:key , text: value } ) );
            return array.filter(a => a.lang !== "undefined" && a.lang )
    }

    const init = () => {
        if (!currentCompany) return;
        const nameHandler = arrayToObj(currentCompany.name);
        const descHandler = arrayToObj(currentCompany.desc);
        const addressHandler = arrayToObj(currentCompany.mainAddress);
        setNameCompany(nameHandler || {});
        setDescCompany(descHandler || {});
        setMainAddress(addressHandler || {});
        if (commercialRegisterationRef.current) {
            commercialRegisterationRef.current.value = currentCompany?.commercialRegisteration || '';
        }
        if (businessFieldRef.current) {
            businessFieldRef.current.value = currentCompany?.businessField || '';
        }
        if (vatNumberRef.current) {
            vatNumberRef.current.value = currentCompany?.vatNumber || '';
        }
        if (mainPhoneRef.current) {
            mainPhoneRef.current.value = currentCompany?.mainPhone || '';
        }
        if (mainEmailRef.current) {
            mainEmailRef.current.value = currentCompany?.mainEmail || '';
        }
        if (websiteUrlRef.current) {
            websiteUrlRef.current.value = currentCompany?.websiteUrl || '';
        }

        if (currentCompany?.logo) {
            setFileLocation(currentCompany.logo);
            setFileKey(currentCompany.logo);
            setSelectedFile({
                name: 'Company Logo',
                type: currentCompany.logo.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/jpeg',
                url: currentCompany.logo
            });
        }
        
        if (currentCompany?.currency && currenciesData?.data) {
            const normalizedCompanyCurrency = String(currentCompany.currency).trim();
            const foundCurrency = currenciesData.data.find(curr => String(curr?.currency_code || '').trim() === normalizedCompanyCurrency);
            if (foundCurrency) {
                setSelectedCurrency({
                    _id: foundCurrency.id,
                    label: String(currentLangId === 'ar' ? foundCurrency.name_ar : foundCurrency.name_en).trim(),
                    value: String(foundCurrency.currency_code || '').trim(),
                    currency_code: String(foundCurrency.currency_code || '').trim()
                });
            }
        }
    }

    const companySnapshot = JSON.stringify({
        id: currentCompanyId,
        name: currentCompany?.name,
        desc: currentCompany?.desc,
        mainAddress: currentCompany?.mainAddress,
        businessField: currentCompany?.businessField,
        commercialRegisteration: currentCompany?.commercialRegisteration,
        vatNumber: currentCompany?.vatNumber,
        mainPhone: currentCompany?.mainPhone,
        mainEmail: currentCompany?.mainEmail,
        websiteUrl: currentCompany?.websiteUrl,
        logo: currentCompany?.logo,
        currency: currentCompany?.currency
    });

    useEffect(() => {
        init();
        setFormLang(currentLangId);
    }, [companySnapshot, currenciesData?.data?.length, currentLangId]);

    useEffect(() => {
        if (location) {
            setFileKey(location);
            setFileLocation(location);
            setSelectedFile({
                name: reqAttach?.name || 'Uploaded file',
                type: reqAttach?.type || 'application/octet-stream',
                url: location
            });
        }
    }, [location]); 

    const submitHandler = (e) =>{
        e.preventDefault();
        const _data = {
            companyId:currentCompanyId,
            companyData:{
                businessField:businessFieldRef.current.value,
                commercialRegisteration:commercialRegisterationRef.current.value,
                desc:objToArray(desCompany) ,
                logo:fileLocation ,
                mainAddress:objToArray(mainAddress) ,
                mainEmail:mainEmailRef.current.value ,
                mainPhone:mainPhoneRef.current.value ,
                name:objToArray(nameCompany),
                vatNumber:vatNumberRef.current.value ,
                websiteUrl:websiteUrlRef.current.value,
                currency: selectedCurrency?.currency_code || null
            }
        }
        putData(`${process.env.REACT_APP_Auth_API_BASE_URL}/tenants/companies/${currentTenantId}`,_data)
    }
    useEffect(()=>{
        if(dataPutResult && !loading){
            refreshTenant()
        }
    },[dataPutResult]);
    return (
         <>
            <div>
                <div className='flex  justify-between items-center gap-2 text-xs text-slate-500 font-bold px-1 '>
                    <div>
                       {t("setting.companySettings")}: {nameCompany?.[currentLangId] || ''}
                    </div>
                    { loading ? <MwSpinnerButton/> : <MwButton 
                                    inGroup = {true}
                                    size='sm'
                                    onClick={submitHandler}
                                    type = 'saveBtn'  >{t("setting.saveChanges")}
                                    </MwButton> }
                </div>
                <div className='p-3 my-3  rounded-lg border border-slate-200'>
                    <form onSubmit={submitHandler}>
                        <FormLangBar formLang={formLang} setFormLang={setFormLang} currentLangList = {currentLangList || []}  />
                        <div className='flex flex-col gap-1 py-1  md:flex-row md:gap-0  '>
                            { (currentLangList || []).map( lang => {
                                return  ( 
                                    <div  key={lang._id} className={` w-full  ${formLang === lang.langCode ? 'block' : 'hidden'} `} >
                                        <MwInputText
                                            label = {t("setting.companyName")}
                                            value={nameCompany?.[lang?.langCode] || ''}
                                            onChange={(e)=>setNameCompany({...nameCompany, [lang.langCode]:e.target.value })}
                                        />
                                        <MwInputText
                                            label = {t("setting.companyActivityDescription")}
                                            value={desCompany?.[lang?.langCode] || ''}
                                            onChange={(e)=>setDescCompany({...desCompany, [lang.langCode]:e.target.value })}
                                        />
                                        <MwTextArea
                                            label={t("setting.companyAddress")}
                                            placeholder={t("setting.enterCompanyAddress")}
                                            value={mainAddress?.[lang.langCode] || ''}
                                            onChange={(e)=>setMainAddress({...mainAddress, [lang.langCode]:e.target.value })}
                                        />
                                    </div>
                                )})}
                        </div>

                        <MwSelector
                            label = {t("setting.mainCurrency")}
                            _data = {formatCurrenciesForSelector()}
                            dataType = 'labelValue'
                            setSelectedItem = {setSelectedCurrency}
                            selectedItem = {selectedCurrency}
                            initalValue = {t("setting.chooseCurrency")}
                            withSearch = {true}
                            withLabel = {true}
                            withAddNew = {false}
                            className = 'mt-3'
                        />
                        <MwInputText
                            label = {t("setting.businessField")}
                            inputRef = {businessFieldRef}
                        />
                        <MwInputText
                            label = {t("setting.commercialRegisterNumber")}
                            inputRef = {commercialRegisterationRef}
                        />
                        <MwInputText
                            label = {t("setting.vatNumber")}
                            inputRef = {vatNumberRef}
                        />
                        
                        <div>
                            <label className="py-2 text-sm font-medium text-gray-900">{t('setting.companyLogo')}</label>
                            <div className="border-2 bg-white border-dashed rounded-lg p-4">
                                <MwInputFile
                                    onChangeHandler={handleFileChange}
                                    acceptTypes="image/*,.webp,.svg,.bmp,.tiff,application/pdf,.pdf"
                                    label={t("setting.clickHereOrChooseFromDevice")}
                                    id="file_upload"
                                    isMultiple={false}
                                    isSmall={false}
                                    loading={fileUploadLoading}
                                />
                                {selectedFile && (
                                    <div className="mt-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="text-sm text-gray-600">{t('setting.selectedFile')}: {selectedFile.name}</p>
                                            <button
                                                onClick={handleRemoveFile}
                                                className="p-1 hover:bg-red-50 rounded-full text-red-500 hover:text-red-700 transition-colors"
                                                title={t("setting.cancelFile")}
                                                disabled={deleteLoading}
                                                type="button"
                                            >
                                                <FaTimes size={16} />
                                            </button>
                                        </div>
                                        {selectedFile.type.startsWith('image/') || selectedFile.name.toLowerCase().endsWith('.webp') ? (
                                                <img src={selectedFile.url} alt="Preview" className="max-w-xs mx-auto h-40 object-contain" />
                                            ) : selectedFile.type === 'application/pdf' && (
                                                <div className="flex items-center justify-center">
                                                    <embed src={selectedFile.url} type="application/pdf" width="100%" height="200px" />
                                                </div>
                                            )}
                                                                                </div>
                                )}
                            </div>
                        </div>
                        <MwInputText
                            label = {t("setting.phoneNumber")}
                            inputRef = {mainPhoneRef}
                                />
                        <MwInputText
                            label = {t("setting.email")}
                            inputRef = {mainEmailRef}
                        />
                        <MwInputText
                            label = {t("setting.websiteUrl")}
                            inputRef = {websiteUrlRef}
                        />

                   
                    </form>
                </div>
            </div>
        </>
    )
}

export default MyCompany
